import React, { useEffect, useMemo, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { AddRounded as AddIcon } from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, SearchField } from "../../../atoms";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import usePagedSearch from "./usePagedSearch";
import PagedOptionList from "./PagedOptionList";

const norm = (s) => String(s || "").toLocaleLowerCase("tr-TR");

/**
 * Listeye öğe (alerji, tanı, şikayet, besin …) eklemek için sağdan açılan
 * çoklu seçim drawer'ı. Seçenekler sunucudan sayfa sayfa gelir (gecikmeli
 * arama + kaydırdıkça yükleme); zaten ekli olanlar "Ekli" olarak pasif
 * görünür. Kaydetme çağıranın işidir.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {React.ReactElement} icon - Başlık ikon çipi ve boş durum ikonu
 * @param {string} [accent] - Başlık vurgu rengi
 * @param {string} title
 * @param {string} [subtitle]
 * @param {({ query: string, page: number }) => Promise<{
 *   items: Array<{key: string, value: string, title: string, subtitle?: string, raw?: any}>,
 *   hasMore: boolean,
 * }>} fetchPage - `value` "Ekli" kontrolünde kullanılan metindir
 * @param {string[]} existing - Zaten ekli değerler
 * @param {(values: string[], options: object[]) => void} onSubmit
 *   Seçilen değerler ve seçenek nesneleri (ham kayıt `raw` içinde)
 * @param {boolean} [loading] - Kaydediliyor
 * @param {string} [searchPlaceholder]
 * @param {string} [noun] - Metinler için ("alerji", "tanı")
 * @param {boolean} [single] - Tek seçim (yeni seçim öncekinin yerine geçer; buton "Seç")
 * @param {boolean} [allowCustom] - Aramada listede olmayan metin "… olarak ekle" ile
 *   serbest öğe olarak seçilebilir (`custom: true`, `raw: null`)
 */
export default function TagPickerDrawer({
  open,
  onClose,
  icon,
  accent,
  title,
  subtitle,
  fetchPage,
  existing = [],
  onSubmit,
  loading = false,
  searchPlaceholder = "Ara",
  noun = "kayıt",
  single = false,
  allowCustom = false,
}) {
  const search = usePagedSearch(open, fetchPage);
  // value → seçenek; sayfalar değişse de seçim korunur
  const [selected, setSelected] = useState(() => new Map());

  useEffect(() => {
    if (open) setSelected(new Map());
  }, [open]);

  const existingSet = useMemo(() => new Set(existing.map(norm)), [existing]);

  const toggle = (o) =>
    setSelected((prev) => {
      if (single) return prev.has(o.value) ? new Map() : new Map([[o.value, o]]);
      const next = new Map(prev);
      if (next.has(o.value)) next.delete(o.value);
      else next.set(o.value, o);
      return next;
    });

  // Serbest metin seçeneği: arama listede birebir yoksa en üstte görünür
  const customText = search.debouncedQuery.trim();
  const customOption =
    allowCustom &&
    customText &&
    !search.items.some((o) => norm(o.title) === norm(customText))
      ? {
          key: `custom:${customText}`,
          value: customText,
          title: `“${customText}” olarak ekle`,
          subtitle: "Listede yoksa serbest metin olarak eklenir",
          raw: null,
          custom: true,
        }
      : null;

  const count = selected.size;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader icon={icon} bgcolor={accent} title={title} subtitle={subtitle} onClose={onClose} />

      <Box sx={{ px: 3, pt: 2, pb: 1.5, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate100}` }}>
        <SearchField
          value={search.query}
          onChange={search.setQuery}
          placeholder={searchPlaceholder}
          isPending={search.searchPending}
          fullWidth
        />
        <Typography sx={{ mt: 1, fontSize: TYPO.label, color: COLORS.slate500 }}>
          {count > 0
            ? single
              ? `Seçilen: ${[...selected.values()][0]?.title}`
              : `${count} ${noun} seçildi`
            : single
              ? `Listeden bir ${noun} seçin.`
              : `Listeden bir veya birden fazla ${noun} seçin.`}
        </Typography>
      </Box>

      <PagedOptionList
        search={search}
        icon={icon}
        noun={noun}
        isSelected={(o) => selected.has(o.value)}
        isAdded={(o) => existingSet.has(norm(o.value))}
        onToggle={toggle}
        disabled={loading}
        leadItems={customOption ? [customOption] : []}
      />

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Vazgeç"
        onSubmit={() => onSubmit?.([...selected.keys()], [...selected.values()])}
        submitLabel={single ? "Seç" : count > 0 ? `Ekle (${count})` : "Ekle"}
        loading={loading}
        loadingLabel="Ekleniyor…"
        disabled={count === 0}
        startIcon={<AddIcon />}
        fullWidth
      />
    </Drawer>
  );
}
